/**
 * labPermissions.ts — Issue #285
 *
 * Canonical permission identifiers for the "实验室与回输" (Lab & Infusion)
 * module. Mirrors the backend `@RequirePermission(resource, action)` decorators
 * declared on the lab controllers in `apps/api/src/modules/lab/controllers/`:
 *
 *   - cell-batch.controller.ts:      lab.cell_batch:read / create / update
 *   - exception.controller.ts:       lab.exception_event:read / update
 *   - infusion.controller.ts:        lab.infusion:read / update
 *     (the POST /verify identity-check endpoint requires lab.infusion:update —
 *      backend does not currently expose a finer `identity_check` action, so
 *      the front-end gates the identity-check entry on the same canonical key
 *      to stay aligned with the backend RBAC contract.)
 *   - qc-order.controller.ts:        lab.qc_order:read / update
 *   - release-order.controller.ts:   lab.release_order:read / update
 *   - sample-record.controller.ts:   lab.sample_record:read / write
 *
 * Re-exports the wildcard-aware `hasPermission` / `hasAnyPermission` helpers
 * from `crmPermissions.ts` so callers in `apps/admin-web/src/pages/lab/*`
 * never fall back to `permissions.includes(...)`, which cannot honour the
 * superuser `*:*`, resource (`<resource>:*`) or action (`*:<action>`)
 * wildcards the backend permissions guard supports.
 */

import { hasAnyPermission, hasPermission } from './crmPermissions';

export { hasPermission, hasAnyPermission };

export const LAB_PERMISSIONS = {
  CELL_BATCH_READ: 'lab.cell_batch:read',
  CELL_BATCH_CREATE: 'lab.cell_batch:create',
  CELL_BATCH_UPDATE: 'lab.cell_batch:update',

  EXCEPTION_EVENT_READ: 'lab.exception_event:read',
  EXCEPTION_EVENT_UPDATE: 'lab.exception_event:update',

  INFUSION_READ: 'lab.infusion:read',
  INFUSION_UPDATE: 'lab.infusion:update',

  QC_ORDER_READ: 'lab.qc_order:read',
  QC_ORDER_UPDATE: 'lab.qc_order:update',

  RELEASE_ORDER_READ: 'lab.release_order:read',
  RELEASE_ORDER_UPDATE: 'lab.release_order:update',

  SAMPLE_RECORD_READ: 'lab.sample_record:read',
  SAMPLE_RECORD_WRITE: 'lab.sample_record:write',
} as const;

export type LabPermission = (typeof LAB_PERMISSIONS)[keyof typeof LAB_PERMISSIONS];

export function canReadCellBatch(granted: readonly string[]): boolean {
  return hasPermission(granted, LAB_PERMISSIONS.CELL_BATCH_READ);
}

export function canUpdateCellBatch(granted: readonly string[]): boolean {
  return hasPermission(granted, LAB_PERMISSIONS.CELL_BATCH_UPDATE);
}

export function canReadExceptionEvent(granted: readonly string[]): boolean {
  return hasPermission(granted, LAB_PERMISSIONS.EXCEPTION_EVENT_READ);
}

export function canUpdateExceptionEvent(granted: readonly string[]): boolean {
  return hasPermission(granted, LAB_PERMISSIONS.EXCEPTION_EVENT_UPDATE);
}

export function canReadInfusion(granted: readonly string[]): boolean {
  return hasPermission(granted, LAB_PERMISSIONS.INFUSION_READ);
}

export function canUpdateInfusion(granted: readonly string[]): boolean {
  return hasPermission(granted, LAB_PERMISSIONS.INFUSION_UPDATE);
}

export function canVerifyInfusionIdentity(granted: readonly string[]): boolean {
  return hasPermission(granted, LAB_PERMISSIONS.INFUSION_UPDATE);
}

export function canReadQcOrder(granted: readonly string[]): boolean {
  return hasPermission(granted, LAB_PERMISSIONS.QC_ORDER_READ);
}

export function canUpdateQcOrder(granted: readonly string[]): boolean {
  return hasPermission(granted, LAB_PERMISSIONS.QC_ORDER_UPDATE);
}

export function canReadReleaseOrder(granted: readonly string[]): boolean {
  return hasPermission(granted, LAB_PERMISSIONS.RELEASE_ORDER_READ);
}

export function canUpdateReleaseOrder(granted: readonly string[]): boolean {
  return hasPermission(granted, LAB_PERMISSIONS.RELEASE_ORDER_UPDATE);
}

export function canReadSampleRecord(granted: readonly string[]): boolean {
  return hasPermission(granted, LAB_PERMISSIONS.SAMPLE_RECORD_READ);
}

export function canWriteSampleRecord(granted: readonly string[]): boolean {
  return hasPermission(granted, LAB_PERMISSIONS.SAMPLE_RECORD_WRITE);
}

export function canSeeLabMenu(granted: readonly string[]): boolean {
  return hasAnyPermission(granted, [
    LAB_PERMISSIONS.CELL_BATCH_READ,
    LAB_PERMISSIONS.QC_ORDER_READ,
    LAB_PERMISSIONS.RELEASE_ORDER_READ,
    LAB_PERMISSIONS.INFUSION_READ,
    LAB_PERMISSIONS.EXCEPTION_EVENT_READ,
    LAB_PERMISSIONS.SAMPLE_RECORD_READ,
  ]);
}
